<template>
  <div>
    <!-- Tabs: Dashboard / Alle Kontakte / Meine Kontakte / Anrufe -->
    <div v-if="!monitorMode" class="mb-5 overflow-x-auto -mx-1 px-1 pb-0.5 pt-1 -mt-1">
      <div class="flex gap-0.5 bg-slate-100/90 dark:bg-slate-800/90 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50 rounded-2xl p-1 shadow-sm min-w-max">
        <button
          @click="setTab('dashboard')"
          class="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap"
          :class="activeTab === 'dashboard' 
            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200/60 dark:ring-slate-600/60' 
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50'"
        >
          <LayoutDashboard class="w-4 h-4" />
          <span>Dashboard</span>
        </button>

        <button
          @click="setTab('all')"
          class="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap"
          :class="activeTab === 'all' 
            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200/60 dark:ring-slate-600/60' 
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50'"
        >
          <BookUser class="w-4 h-4" />
          <span>Alle</span>
          <span 
            class="text-xs px-1.5 py-0.5 rounded-full font-medium"
            :class="activeTab === 'all' 
              ? 'bg-slate-100 dark:bg-slate-600 text-slate-600 dark:text-slate-300' 
              : 'bg-slate-200/70 dark:bg-slate-700 text-slate-500 dark:text-slate-400'"
          >{{ pagination.total || 0 }}</span>
        </button>

        <button
          @click="switchToMyContacts"
          class="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 whitespace-nowrap"
          :class="activeTab === 'my' 
            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200/60 dark:ring-slate-600/60' 
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50'"
        >
          <Star 
            class="w-4 h-4 transition-colors"
            :class="activeTab === 'my' ? 'fill-amber-400 text-amber-400' : ''" 
          />
          <span>Meine</span>
        </button>

        <button
          @click="switchToCallsTab"
          class="flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 relative whitespace-nowrap"
          :class="activeTab === 'calls' 
            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm ring-1 ring-slate-200/60 dark:ring-slate-600/60' 
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-white/60 dark:hover:bg-slate-700/50'"
        >
          <PhoneCall class="w-4 h-4" />
          <span>Anrufe</span>
          <!-- Missed calls badge -->
          <span 
            v-if="missedCallsCount > 0"
            class="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full px-1 shadow-sm animate-pulse"
          >
            {{ missedCallsCount > 99 ? '99+' : missedCallsCount }}
          </span>
          <!-- Assignment badge -->
          <span 
            v-else-if="callAssignmentCount > 0"
            class="absolute -top-1 -right-1 min-w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-blue-500 rounded-full px-1 shadow-sm animate-pulse"
          >
            {{ callAssignmentCount > 9 ? '9+' : callAssignmentCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- Dashboard Tab -->
    <Dashboard
      v-if="activeTab === 'dashboard'"
      :monitorMode="monitorMode"
      @open-contact="openContactByEmail"
      @open-notes="openCallNotes"
      @switch-tab="(tab) => setTab(tab)"
      @toggle-monitor-mode="toggleMonitorMode"
    />

    <!-- All Contacts Tab: lazy-mount on first visit, then kept alive via v-show -->
    <AllContactsTab
      v-if="mountedTabs.has('all')"
      v-show="activeTab === 'all'"
      @select-contact="(contact) => selectedContact = contact"
      @quick-call="handleQuickCall"
    />

    <!-- My Contacts Tab: lazy-mount on first visit, then kept alive via v-show -->
    <MyContactsTab
      v-if="mountedTabs.has('my')"
      v-show="activeTab === 'my'"
      @select-contact="(contact) => selectedContact = contact"
    />

    <!-- Calls Tab: lazy-mount on first visit, then kept alive via v-show -->
    <CallsTab
      v-if="mountedTabs.has('calls')"
      v-show="activeTab === 'calls'"
      ref="callsTabRef"
      @select-contact="(contact) => selectedContact = contact"
      @open-call-notes="openCallNotes"
    />

    <!-- Contact Detail Modal -->
    <ContactDetail
      v-if="selectedContact"
      :contact="selectedContact"
      @close="selectedContact = null"
    />

    <!-- Call Notes Modal -->
    <CallNotes
      v-if="showCallNotes && selectedCallForNotes"
      :call="selectedCallForNotes"
      :queueEmail="selectedCallQueueEmail"
      :queueName="selectedCallQueueName"
      @close="closeCallNotes"
      @saved="handleNoteSaved"
    />

    <!-- Assignment Toast Notification -->
    <Transition name="slide-up">
      <div 
        v-if="assignmentToast"
        class="fixed bottom-6 right-6 z-50 max-w-sm bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-700/50 rounded-2xl shadow-2xl overflow-hidden cursor-pointer"
        @click="dismissAssignmentToast"
      >
        <div class="p-4">
          <div class="flex items-start gap-3">
            <div class="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center flex-shrink-0">
              <span class="text-lg">📋</span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-slate-900 dark:text-white">Anruf zugewiesen</p>
              <p class="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                {{ assignmentToast.assignedByName }} hat dir einen Anruf von <strong>{{ assignmentToast.callerName }}</strong> zugewiesen
              </p>
              <p v-if="assignmentToast.queueName" class="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                📞 {{ assignmentToast.queueName }}
              </p>
              <p v-if="assignmentToast.notePreview" class="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
                „{{ assignmentToast.notePreview }}"
              </p>
            </div>
            <button 
              @click.stop="dismissAssignmentToast"
              class="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
            >
              <X class="w-4 h-4 text-slate-400" />
            </button>
          </div>
        </div>
        <div class="h-1 bg-blue-500 animate-shrink"></div>
      </div>
    </Transition>

    <!-- Error Toast -->
    <Transition name="slide-up">
      <div 
        v-if="exportError"
        class="fixed bottom-6 left-6 z-50 max-w-sm bg-white dark:bg-slate-800 border border-red-200 dark:border-red-700/50 rounded-2xl shadow-2xl overflow-hidden cursor-pointer"
        @click="exportError = null"
      >
        <div class="p-4 flex items-center gap-3">
          <div class="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center flex-shrink-0">
            <span class="text-lg">⚠️</span>
          </div>
          <p class="text-sm font-medium text-red-700 dark:text-red-400">{{ exportError }}</p>
        </div>
      </div>
    </Transition>

    <!-- Quick Call Disambiguation Modal -->
    <Teleport to="body">
      <div
        v-if="showQuickCallModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        @click.self="showQuickCallModal = false"
      >
        <div class="absolute inset-0 bg-black/50 backdrop-blur-sm"></div>
        <div class="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
          <div class="px-5 pt-5 pb-3 border-b border-slate-200/50 dark:border-slate-700/50">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <Phone class="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 class="text-base font-bold text-slate-800 dark:text-slate-200">Schnellanruf</h3>
                <p class="text-xs text-slate-500 dark:text-slate-400">Mehrere Treffer für "{{ quickCallSearchTerm }}" — bitte wählen:</p>
              </div>
            </div>
          </div>
          <div class="max-h-80 overflow-y-auto p-2">
            <button
              v-for="c in quickCallCandidates"
              :key="c.email"
              @click="quickCallContact(c)"
              class="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors text-left"
            >
              <div 
                class="w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                :class="(() => {
                  const palette = ['from-blue-400 to-blue-600','from-violet-400 to-purple-600','from-emerald-400 to-green-600','from-orange-400 to-orange-600','from-pink-400 to-rose-600','from-teal-400 to-cyan-600','from-indigo-400 to-indigo-700','from-amber-400 to-orange-500','from-red-400 to-rose-600','from-sky-400 to-blue-500'];
                  const seed = (c.display_name || c.email || '').split('').reduce((a, ch) => a + ch.charCodeAt(0), 0);
                  return palette[seed % palette.length];
                })()"
              >
                {{ (c.given_name?.[0] || '') + (c.surname?.[0] || c.display_name?.[0] || '') }}
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-semibold text-sm text-slate-800 dark:text-slate-200 truncate">{{ c.display_name }}</span>
                  <span v-if="c.entra_id" class="px-1.5 py-0.5 text-[9px] font-bold bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded">Teams</span>
                  <span v-else class="px-1.5 py-0.5 text-[9px] font-bold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded">Extern</span>
                </div>
                <p v-if="c.company_name" class="text-xs text-slate-500 dark:text-slate-400 truncate">{{ c.company_name }}</p>
                <p class="text-xs text-slate-400 dark:text-slate-500 truncate">
                  {{ c.entra_id && !c.email?.endsWith('@mailbox-contact.local') ? c.email : (c.mobile_phone || getFirstPhone(c)) }}
                </p>
              </div>
              <div class="flex-shrink-0">
                <div v-if="c.entra_id && !c.email?.endsWith('@mailbox-contact.local')" class="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                  <Phone class="w-4 h-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div v-else class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
                  <Phone class="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </button>
          </div>
          <div class="px-5 py-3 border-t border-slate-200/50 dark:border-slate-700/50 flex justify-end">
            <button
              @click="showQuickCallModal = false"
              class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >Abbrechen</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { X, Phone, LayoutDashboard, BookUser, Star, PhoneCall } from 'lucide-vue-next';
import { 
  contacts, pagination,
  missedCallsCount, fetchMissedCallsCount, markMissedCallsSeen,
  callAssignments, callAssignmentCount, clearAssignmentCount,
  fetchMyContacts
} from '../websocketService';
import { authenticatedFetch } from '../oauth2-config';
import { loadCallPreferencesFromServer } from '../localStorageService';
import { getMonitorMode, setMonitorMode } from '../localStorageService';
import { makePhoneCall } from '../callService';
import ContactDetail from './ContactDetail.vue';
import CallNotes from './CallNotes.vue';
import Dashboard from './Dashboard.vue';
import AllContactsTab from './AllContactsTab.vue';
import MyContactsTab from './MyContactsTab.vue';
import CallsTab from './CallsTab.vue';

const API_URL = process.env.VUE_APP_API_URL || '__VUE_APP_API_URL__';

const activeTab = ref('dashboard');
// Track which tabs have been mounted at least once (lazy-mount + keep-alive via v-show)
const mountedTabs = ref(new Set());

// Switch tab: lazy-mount on first visit, keep alive via v-show after
const setTab = (tab) => {
  mountedTabs.value.add(tab);
  activeTab.value = tab;
};

// Monitor Mode state
const monitorMode = ref(getMonitorMode());
const emit = defineEmits(['monitor-mode-changed']);

const toggleMonitorMode = () => {
  monitorMode.value = !monitorMode.value;
  setMonitorMode(monitorMode.value);
  if (monitorMode.value) {
    activeTab.value = 'dashboard';
  }
  emit('monitor-mode-changed', monitorMode.value);
};

// Shared modal state
const selectedContact = ref(null);
const showCallNotes = ref(false);
const selectedCallForNotes = ref(null);
const selectedCallQueueEmail = ref('');
const selectedCallQueueName = ref('');

// Assignment toast notification
const assignmentToast = ref(null);
let assignmentToastTimeout = null;

// Export error toast
const exportError = ref(null);

// Quick Call state
const showQuickCallModal = ref(false);
const quickCallCandidates = ref([]);
const quickCallSearchTerm = ref('');

// Calls tab ref for forwarding events
const callsTabRef = ref(null);

// Open contact details by email
const openContactByEmail = async (email) => {
  if (!email) return;
  
  let contact = contacts.value.find(c => c.email?.toLowerCase() === email.toLowerCase());
  
  if (!contact) {
    try {
      const response = await authenticatedFetch(`${API_URL}/api/contacts/by-email/${encodeURIComponent(email)}`);
      if (response.ok) {
        const data = await response.json();
        if (data) {
          contact = data;
        }
      }
    } catch (error) {
      console.error('Could not find contact:', error);
    }
  }
  
  if (contact) {
    selectedContact.value = contact;
  }
};

// Call Notes
const openCallNotes = (call) => {
  selectedCallForNotes.value = call;
  selectedCallQueueEmail.value = call.call_queue?.queue_email || '';
  selectedCallQueueName.value = call.call_queue?.queue_name || '';
  showCallNotes.value = true;
};

const closeCallNotes = () => {
  showCallNotes.value = false;
  selectedCallForNotes.value = null;
  selectedCallQueueEmail.value = '';
  selectedCallQueueName.value = '';
};

const handleNoteSaved = (note) => {
  // Forward to CallsTab if active
  if (callsTabRef.value) {
    callsTabRef.value.handleNoteSaved(note);
  }
};

// Assignment toast
const dismissAssignmentToast = () => {
  assignmentToast.value = null;
  if (assignmentToastTimeout) clearTimeout(assignmentToastTimeout);
};

// Tab switching helpers
const switchToMyContacts = () => {
  setTab('my');
};

const switchToCallsTab = async () => {
  setTab('calls');
  if (missedCallsCount.value > 0) {
    await markMissedCallsSeen();
  }
};

// Quick Call helpers
const getFirstPhone = (contact) => {
  const phones = contact.business_phones;
  const arr = typeof phones === 'string' ? JSON.parse(phones || '[]') : (phones || []);
  return arr[0] || null;
};

const quickCallContact = (contact) => {
  showQuickCallModal.value = false;
  
  const hasValidEmail = contact.email && !contact.email.endsWith('@mailbox-contact.local');
  const isEntraUser = !!contact.entra_id;
  
  if (isEntraUser && hasValidEmail) {
    const url = `https://teams.microsoft.com/l/call/0/0?users=${encodeURIComponent(contact.email)}`;
    window.open(url, '_blank');
  } else {
    const phone = contact.mobile_phone || getFirstPhone(contact);
    if (phone) {
      makePhoneCall(phone);
    }
  }
};

// Handle quick call event from AllContactsTab
const handleQuickCall = ({ searchTerm, candidates }) => {
  if (candidates.length === 1) {
    quickCallContact(candidates[0]);
  } else {
    quickCallSearchTerm.value = searchTerm;
    quickCallCandidates.value = candidates;
    showQuickCallModal.value = true;
  }
};

// Ctrl+K global keyboard shortcut + ESC for monitor mode
const handleGlobalKeydown = (e) => {
  if (e.key === 'Escape' && monitorMode.value) {
    e.preventDefault();
    toggleMonitorMode();
    return;
  }
  if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
    e.preventDefault();
    if (monitorMode.value) {
      toggleMonitorMode();
    }
    setTab('all');
  }
};

// Watch for new assignment notifications (show toast)
watch(callAssignmentCount, (newCount, oldCount) => {
  if (newCount > oldCount && callAssignments.value.length > 0) {
    const latest = callAssignments.value[0];
    assignmentToast.value = latest;
    if (assignmentToastTimeout) clearTimeout(assignmentToastTimeout);
    assignmentToastTimeout = window.setTimeout(() => {
      assignmentToast.value = null;
    }, 8000);
  }
});

// Clear assignment badge when switching to calls tab
watch(activeTab, (tab) => {
  if (tab === 'calls' && callAssignmentCount.value > 0) {
    clearAssignmentCount();
  }
});

onMounted(() => {
  loadCallPreferencesFromServer();
  fetchMissedCallsCount();
  document.addEventListener('keydown', handleGlobalKeydown);

  if (monitorMode.value) {
    // Don't use setTab for dashboard — it doesn't use lazy-mount
    activeTab.value = 'dashboard';
    emit('monitor-mode-changed', true);
  }
});

onUnmounted(() => {
  document.removeEventListener('keydown', handleGlobalKeydown);
});
</script>

<style scoped>
/* Assignment toast slide-up animation */
.slide-up-enter-active {
  transition: all 0.3s ease-out;
}
.slide-up-leave-active {
  transition: all 0.2s ease-in;
}
.slide-up-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Toast progress bar shrink animation */
@keyframes shrink {
  from { width: 100%; }
  to { width: 0%; }
}
.animate-shrink {
  animation: shrink 8s linear forwards;
}
</style>
